import { jsx as _jsx } from "react/jsx-runtime";
import { useConsentService } from "@aviv-csnt/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { createContext, useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { usePrefillDataCookie } from "@/components/contacting/ContactForm/usePrefillDataCookie";
import { useIsEnrichedContactFormFields } from "@/components/contacting/hooks/useIsEnrichedContactFormFields";
import { useIsPhoneNumberValidation } from "@/components/contacting/hooks/useIsPhoneNumberValidation";
import { useSimilarListings } from "@/components/SimilarListings/useSimilarListings";
import { ModalIds } from "@/constants";
import { useIsFullNameForm } from "@/contexts/contact/internal/useIsFullNameForm";
import { useLeadValue, useModal, usePrefill, useTrackingLeadInterestMail, useTrackingLegacyInterest, } from "@/hooks";
import { useI18n } from "@/shared/hooks";
import { getFeaturesEnabled, getFilteredObject, hasCompleteUserInfo, } from "@/utils";
import { useValidationSchema } from "@/components/contacting/hooks/useValidationSchema";
import { useClassified } from "../classified";
import { formatPhoneNumbersToArray } from "@/components/contacting/utils/formatPhoneNumbersToArray";
export const ContactContext = createContext(null);
export const ContactProvider = ({ children, contactSections, crsBaseUrl, }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q;
    const { isOpen, toggle, close: closeContactModal, } = useModal(false, ModalIds.CONTACT);
    const { isOpen: suggestPriceIsOpen, toggle: toggleSuggestPricePriceModal } = useModal(false, ModalIds.PRICE);
    const { isOpen: isMultiInquiryModalOpen, toggle: toggleMultiInquiryModal, close: closeMultiInquiryModal, } = useModal(false, ModalIds.MULTI_INQUIRY);
    const { isOpen: isPersonalDataModalOpen, toggle: togglePersonalDataModal } = useModal(false, ModalIds.PERSONAL_DATA);
    const { isOpen: isMobileLegalNoticeSectionModalOpen, toggle: toggleMobileLegalNoticeSectionModal, } = useModal(false, ModalIds.MOBILE_LEGAL_NOTICE_SECTION);
    const legacyTrackingData = (_a = useClassified()) === null || _a === void 0 ? void 0 : _a.legacyTracking;
    const hasLegacyTrackingData = !!legacyTrackingData;
    const locationEnabled = (_d = (_c = (_b = contactSections === null || contactSections === void 0 ? void 0 : contactSections.contactForm) === null || _b === void 0 ? void 0 : _b.customFields) === null || _c === void 0 ? void 0 : _c.locationEnabled) !== null && _d !== void 0 ? _d : false;
    const phoneNumbers = (_e = contactSections === null || contactSections === void 0 ? void 0 : contactSections.contactCard) === null || _e === void 0 ? void 0 : _e.phoneNumbers;
    const formattedPhoneNumbers = formatPhoneNumbersToArray(phoneNumbers);
    const { t } = useI18n();
    const defaultMessage = t("contact.form.subject_placeholder");
    const [userData, setUserData] = useState(null);
    const [userCookieData, setUserCookieData] = useState(null);
    const [userInfoComplete, setUserInfoComplete] = useState(false);
    const [showImageHeaderSection, setShowImageHeaderSection] = useState(false);
    const { similarListings, isFetched: isSimilarListingsFetched, prefetch: prefetchSimilarListings, } = useSimilarListings();
    const { phoneNumberRequired, messageRequired } = getFeaturesEnabled();
    const isPrivateOwner = (_g = (_f = contactSections === null || contactSections === void 0 ? void 0 : contactSections.contactForm) === null || _f === void 0 ? void 0 : _f.contactCard.isPrivateOwner) !== null && _g !== void 0 ? _g : false;
    const isFullNameForm = useIsFullNameForm(isPrivateOwner);
    const isEnrichedRentalContactLeads = useIsEnrichedContactFormFields();
    const isPhoneNumberE164ValidationEnabled = useIsPhoneNumberValidation();
    const { getPrefillDataCookie, erasePrefillDataCookie } = usePrefillDataCookie();
    const { consentItems, isConsentGranted } = useConsentService();
    const [avZone, setAvZone] = useState(null);
    const deletePrefillDataCookie = () => {
        const cookieConsent = "Contact Auto-fill";
        if (Object.values(consentItems).some((item) => item.name === cookieConsent && !item.consent)) {
            erasePrefillDataCookie();
        }
    };
    const validationSchemaOptions = {
        displayOwnerFlag: (_j = (_h = contactSections === null || contactSections === void 0 ? void 0 : contactSections.contactForm) === null || _h === void 0 ? void 0 : _h.displayOwnerFlag) !== null && _j !== void 0 ? _j : false,
        customFields: (_m = (_l = (_k = contactSections === null || contactSections === void 0 ? void 0 : contactSections.contactForm) === null || _k === void 0 ? void 0 : _k.customFields) === null || _l === void 0 ? void 0 : _l.locationEnabled) !== null && _m !== void 0 ? _m : false,
        phoneNumberRequired: phoneNumberRequired,
        isMessageRequired: messageRequired,
        isPrivateOwner: isPrivateOwner,
        isSuggestPriceForm: suggestPriceIsOpen,
        isStickyContactForm: isEnrichedRentalContactLeads,
        isSimplifiedContactForm: isPersonalDataModalOpen,
        isSingleFieldNameInput: isFullNameForm,
        isPhoneNumberE164ValidationEnabled: isPhoneNumberE164ValidationEnabled,
    };
    const schema = useValidationSchema(t, validationSchemaOptions);
    const methods = useForm({
        resolver: zodResolver(schema),
        mode: "onBlur",
        defaultValues: {
            message: defaultMessage,
        },
        shouldFocusError: false,
    });
    /**
     * This effect is used to reset the message field to the default message after the modal is closed.
     * Reason for it is that there is only single instance of ContactModal used within ContactContext
     * therefor state is shared. With this reset we want to ensure that we have default message on
     * various places when modal is initialized.
     */
    useEffect(() => {
        if (isOpen === false) {
            methods.setValue("message", defaultMessage);
        }
    }, [isOpen, methods, defaultMessage]);
    useEffect(() => {
        if (isConsentGranted) {
            deletePrefillDataCookie();
            const prefillDataCookie = getPrefillDataCookie();
            if (prefillDataCookie) {
                setUserCookieData(prefillDataCookie);
            }
            else {
                setUserCookieData(null);
            }
        }
        // we want to run the Effect just once the GDPR is ready
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isConsentGranted]);
    const { seekerProfileForRentalOnly } = getFeaturesEnabled();
    const isRent = ((_p = (_o = useClassified()) === null || _o === void 0 ? void 0 : _o.tracking) === null || _p === void 0 ? void 0 : _p.av_distribution_type_global) === "1";
    // FIXME remove this logic when this is handled on the bff (https://avivgroup.atlassian.net/browse/ASC-3334)
    const enableShareSeekerProfile = !seekerProfileForRentalOnly ||
        (seekerProfileForRentalOnly && isRent && isPrivateOwner);
    usePrefill({
        crsBaseUrl,
        onLoad: (userData) => {
            if (userData) {
                const locationKeys = [
                    "city",
                    "street",
                    "zipCode",
                ];
                const excludedKeys = locationEnabled ? [] : locationKeys;
                const filteredUserData = getFilteredObject(userData, excludedKeys);
                setUserData(filteredUserData);
            }
            setUserInfoComplete(hasCompleteUserInfo(userData, isFullNameForm));
        },
    });
    const { fetchLeadValue } = getFeaturesEnabled();
    const leadValue = useLeadValue({
        crsBaseUrl,
        legacyId: contactSections === null || contactSections === void 0 ? void 0 : contactSections.legacyId,
        shouldSendLeadValue: fetchLeadValue &&
            isOpen &&
            !!((_q = contactSections === null || contactSections === void 0 ? void 0 : contactSections.contactForm) === null || _q === void 0 ? void 0 : _q.shouldSendLeadValue),
    });
    const [tracking, setTracking] = useState(null);
    const trackLeadInterestMail = useTrackingLeadInterestMail({
        av_zone: tracking === null || tracking === void 0 ? void 0 : tracking.av_zone,
        av_feature: tracking === null || tracking === void 0 ? void 0 : tracking.av_feature,
        av_contact_info_field: userInfoComplete ? "completed" : "not_completed",
    });
    const trackingLegacyInterest = useTrackingLegacyInterest({
        type: "mail",
        // @ts-ignore
        infos: tracking === null || tracking === void 0 ? void 0 : tracking.legacyTrackingInfos,
    });
    const [contactTrackingSent, setContactTrackingSent] = useState(false);
    const [suggestPriceTrackingSent, setSuggestPriceTrackingSent] = useState(false);
    useEffect(() => {
        if (isOpen && tracking && !contactTrackingSent) {
            trackLeadInterestMail();
            if (hasLegacyTrackingData)
                trackingLegacyInterest(); //legacy GA3 for harvest
            setContactTrackingSent(true);
        }
        if (suggestPriceIsOpen && tracking && !suggestPriceTrackingSent) {
            trackLeadInterestMail();
            if (hasLegacyTrackingData)
                trackingLegacyInterest();
            setSuggestPriceTrackingSent(true);
        }
    }, [
        isOpen,
        suggestPriceIsOpen,
        trackLeadInterestMail,
        trackingLegacyInterest, //legacy GA3 for harvest
        tracking,
        contactTrackingSent,
        suggestPriceTrackingSent,
        hasLegacyTrackingData,
    ]);
    const toggleModal = useCallback((trackingState) => {
        if (trackingState &&
            tracking &&
            trackingState.av_zone !== tracking.av_zone) {
            setContactTrackingSent(false);
        }
        // @ts-ignore
        setAvZone(trackingState.av_zone);
        toggle();
        if (trackingState) {
            setTracking(trackingState);
        }
    }, [tracking, toggle]);
    const toggleSuggestPriceModal = useCallback((trackingState) => {
        // @ts-ignore
        setAvZone(trackingState.av_zone);
        toggleSuggestPricePriceModal();
        if (trackingState) {
            setTracking(trackingState);
        }
    }, [toggleSuggestPricePriceModal, setTracking]);
    const triggerImageHeaderSection = useCallback(() => {
        setShowImageHeaderSection(true);
    }, []);
    const resetImageHeaderSection = useCallback(() => {
        setShowImageHeaderSection(false);
    }, []);
    const closeModal = useCallback(() => {
        resetImageHeaderSection();
        closeContactModal();
        setTracking(null);
        setContactTrackingSent(false);
        setSuggestPriceTrackingSent(false);
    }, [closeContactModal, setTracking, resetImageHeaderSection]);
    const contextValue = useMemo(() => ({
        contactTracking: Object.assign(Object.assign({}, (fetchLeadValue && leadValue && { leadValue })), { trackingLegacyInfos: tracking === null || tracking === void 0 ? void 0 : tracking.legacyTrackingInfos, unitId: tracking === null || tracking === void 0 ? void 0 : tracking.unitId }),
        contactSections,
        toggleModal,
        suggestPriceIsOpen,
        toggleSuggestPriceModal,
        avZone,
        methods,
        closeModal,
        isOpen,
        defaultMessage,
        userInfoComplete,
        userData,
        setUserData,
        userCookieData,
        enableShareSeekerProfile,
        showImageHeaderSection,
        triggerImageHeaderSection,
        resetImageHeaderSection,
        toggleMultiInquiryModal,
        closeMultiInquiryModal,
        toggleMobileLegalNoticeSectionModal,
        togglePersonalDataModal,
        isMultiInquiryModalOpen,
        isMobileLegalNoticeSectionModalOpen,
        isPersonalDataModalOpen,
        schema,
        similarListings,
        prefetchSimilarListings,
        isSimilarListingsFetched,
        isFullNameForm,
        phoneNumbers: formattedPhoneNumbers,
    }), [
        contactSections,
        toggleModal,
        suggestPriceIsOpen,
        toggleSuggestPriceModal,
        avZone,
        methods,
        closeModal,
        isOpen,
        leadValue,
        defaultMessage,
        userInfoComplete,
        userData,
        setUserData,
        userCookieData,
        tracking,
        enableShareSeekerProfile,
        fetchLeadValue,
        showImageHeaderSection,
        triggerImageHeaderSection,
        resetImageHeaderSection,
        toggleMultiInquiryModal,
        closeMultiInquiryModal,
        toggleMobileLegalNoticeSectionModal,
        togglePersonalDataModal,
        isMultiInquiryModalOpen,
        isMobileLegalNoticeSectionModalOpen,
        isPersonalDataModalOpen,
        schema,
        similarListings,
        prefetchSimilarListings,
        isSimilarListingsFetched,
        isFullNameForm,
        formattedPhoneNumbers,
    ]);
    return (
    // @ts-ignore
    _jsx(ContactContext.Provider, { value: contextValue, children: _jsx(FormProvider, Object.assign({}, methods, { children: children })) }));
};
